import { useEffect, useRef, useState } from 'react';

// Número de WhatsApp no formato internacional (sem + ou espaços)
const WHATSAPP_NUMBER = '5511984095840';

// Link direto para o WhatsApp (botão lateral)
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Olá, gostaria de avaliar meu caso.')}`;

// Áreas de interesse — correspondência exata com os títulos da seção de atuação
const AREAS_DE_INTERESSE = [
  'Acidente de Trabalho',
  'Doença Ocupacional',
  'Burnout e Depressão',
  'Dores/lesões por esforço no trabalho',
  'Reintegração e Estabilidade',
];

export default function CtaSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    area: '',
    message: '',
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // Ao clicar em Enviar: monta a mensagem e abre o WhatsApp em nova aba
  const handleSubmit = (e: React.FormEvent) => {
    // Impede o comportamento padrão do formulário (sem reload, sem fetch)
    e.preventDefault();

    // Monta a mensagem com estrutura exata solicitada
    const rawMessage =
      `Nome Completo: ${formData.name} | E-mail: ${formData.email} | Área de Interesse: ${formData.area} | Motivo do Contato: ${formData.message}`;

    // Codifica a mensagem para uso seguro na URL
    const encodedMessage = encodeURIComponent(rawMessage);

    // Abre o WhatsApp Web/App em nova aba com a mensagem já preenchida
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, '_blank');
  };

  return (
    <section id="cta" className="bg-brand-black py-20 md:py-28 px-4 md:px-8">
      <div
        ref={ref}
        className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center"
      >
        {/* Left: CTA Text */}
        <div
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0)' : 'translateX(-40px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <span className="text-brand-gold font-sans text-xs font-semibold tracking-widest uppercase">Fale Conosco</span>
          <h2 className="font-serif text-white text-3xl md:text-5xl font-bold mt-3 leading-tight">
            Fale com um advogado{' '}
            <em className="not-italic text-brand-gold">especialista</em>{' '}
            agora
          </h2>
          <p className="font-sans text-white/60 text-base md:text-lg leading-relaxed mt-6">
            Não deixe seus direitos serem violados. Entre em contato agora e receba uma análise gratuita do seu caso. Atendimento online, rápido e humanizado.
          </p>

          <div className="mt-8 space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full border border-brand-gold/30 flex-shrink-0">
                <i className="ri-check-line text-brand-gold text-base" />
              </div>
              <span className="font-sans text-white/70 text-sm">Análise gratuita do seu caso</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full border border-brand-gold/30 flex-shrink-0">
                <i className="ri-check-line text-brand-gold text-base" />
              </div>
              <span className="font-sans text-white/70 text-sm">Atendimento 100% online</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full border border-brand-gold/30 flex-shrink-0">
                <i className="ri-check-line text-brand-gold text-base" />
              </div>
              <span className="font-sans text-white/70 text-sm">Sem custo inicial para consulta</span>
            </div>
          </div>

          <div className="mt-10 flex justify-center md:justify-start">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center gap-3 bg-brand-gold text-brand-black font-sans font-semibold text-base px-8 py-4 rounded-full hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl" />
              Falar no WhatsApp agora
            </a>
          </div>

          <p className="font-sans text-white/30 text-xs mt-4">
            Atendimento conforme as normas da OAB. Seg–Sex: 09h às 18h.
          </p>
        </div>

        {/* Right: Form */}
        <div
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0)' : 'translateX(40px)',
            transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
          }}
        >
          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 md:p-10">
            <h3 className="font-serif text-white text-2xl font-bold mb-2">Avaliar meu caso</h3>
            <p className="font-sans text-white/50 text-sm mb-8">
              Preencha o formulário e você será direcionado ao WhatsApp com sua mensagem pronta.
            </p>

            <form onSubmit={handleSubmit} id="avaliacao-caso">
              <div className="space-y-4">

                {/* Campo: Nome Completo */}
                <div>
                  <label className="block font-sans text-white/60 text-xs mb-1.5 tracking-wide">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Seu nome completo"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-sans text-sm placeholder-white/30 focus:outline-none focus:border-brand-gold/60 transition-colors duration-300"
                  />
                </div>

                {/* Campo: E-mail */}
                <div>
                  <label className="block font-sans text-white/60 text-xs mb-1.5 tracking-wide">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="seu@email.com"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-sans text-sm placeholder-white/30 focus:outline-none focus:border-brand-gold/60 transition-colors duration-300"
                  />
                </div>

                {/* Campo: Área de Interesse — opções idênticas aos títulos da seção de atuação */}
                <div>
                  <label className="block font-sans text-white/60 text-xs mb-1.5 tracking-wide">
                    Área de Interesse *
                  </label>
                  <div className="relative">
                    <select
                      name="area"
                      required
                      value={formData.area}
                      onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                      className="w-full appearance-none bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm font-sans focus:outline-none focus:border-brand-gold/60 transition-colors duration-300 cursor-pointer"
                      style={{ color: formData.area ? '#ffffff' : 'rgba(255,255,255,0.3)' }}
                    >
                      <option value="" disabled style={{ color: '#888', background: '#1a1a1a' }}>
                        Selecione uma área
                      </option>
                      {AREAS_DE_INTERESSE.map((area) => (
                        <option key={area} value={area} style={{ color: '#ffffff', background: '#1a1a1a' }}>
                          {area}
                        </option>
                      ))}
                    </select>
                    {/* Ícone de seta customizado */}
                    <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-down-s-line text-white/40 text-base" />
                    </div>
                  </div>
                </div>

                {/* Campo: Mensagem */}
                <div>
                  <label className="block font-sans text-white/60 text-xs mb-1.5 tracking-wide">
                    Mensagem *
                  </label>
                  <textarea
                    name="message"
                    required
                    value={formData.message}
                    onChange={(e) => {
                      // Limita a 500 caracteres
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, message: e.target.value });
                      }
                    }}
                    placeholder="Conte brevemente o que aconteceu..."
                    rows={4}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-sans text-sm placeholder-white/30 focus:outline-none focus:border-brand-gold/60 transition-colors duration-300 resize-none"
                  />
                  <p className="text-white/30 text-xs mt-1 text-right">{formData.message.length}/500</p>
                </div>
              </div>

              {/* Botão de envio: abre WhatsApp com mensagem montada */}
              <button
                type="submit"
                className="mt-6 w-full bg-brand-gold text-brand-black font-sans font-semibold text-base py-4 rounded-xl hover:bg-white transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-whatsapp-line text-xl" />
                Enviar pelo WhatsApp
              </button>

              <p className="font-sans text-white/30 text-xs mt-3 text-center">
                Ao clicar, você será redirecionado ao WhatsApp com sua mensagem pronta.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
